import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { FlaskConical } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import LegalLink from "@/components/LegalDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function Shell({ title, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 hero-grid px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
            <FlaskConical className="w-6 h-6 text-white" />
          </div>
          <span className="font-head font-bold text-xl text-white">MOLAR KİMYA</span>
        </Link>
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="font-head text-2xl font-bold text-slate-900 mb-6">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Giriş başarılı");
      navigate(u.role === "admin" ? "/yonetim" : "/hesabim");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Shell title="Giriş Yap">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>E-posta</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="mt-1.5" data-testid="login-email" />
        </div>
        <div>
          <Label>Şifre</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="mt-1.5" data-testid="login-password" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700 h-11"
          data-testid="login-submit">{loading ? "..." : "Giriş Yap"}</Button>
      </form>
      <div className="flex justify-between mt-5 text-sm">
        <Link to="/sifremi-unuttum" className="text-slate-500 hover:text-teal-600">Şifremi unuttum</Link>
        <Link to="/kayit" className="text-teal-600 hover:underline">Hesap oluştur</Link>
      </div>
    </Shell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", telefon: "", firma: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(f);
      toast.success("Hesabınız oluşturuldu");
      navigate("/hesabim");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Shell title="Hesap Oluştur">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Ad Soyad</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} required className="mt-1.5" data-testid="reg-name" /></div>
        <div><Label>E-posta</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required className="mt-1.5" data-testid="reg-email" /></div>
        <div><Label>Şifre</Label><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} required className="mt-1.5" data-testid="reg-password" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Telefon</Label><Input value={f.telefon} onChange={(e) => set("telefon", e.target.value)} className="mt-1.5" data-testid="reg-telefon" /></div>
          <div><Label>Firma (ops.)</Label><Input value={f.firma} onChange={(e) => set("firma", e.target.value)} className="mt-1.5" data-testid="reg-firma" /></div>
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700 h-11" data-testid="reg-submit">
          {loading ? "..." : "Hesap Oluştur"}</Button>
      </form>
      <p className="text-xs text-slate-500 leading-relaxed mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3"
        data-testid="kvkk-info-register">
        Üyeliğiniz kapsamında paylaştığınız kişisel veriler, hesabınızın oluşturulması ve taleplerinizin
        yürütülmesi amacıyla işlenir. Ayrıntılı bilgi için{" "}
        <LegalLink slug="aydinlatma">Aydınlatma Metni</LegalLink> ve{" "}
        <LegalLink slug="gizlilik">Gizlilik Politikası</LegalLink>'nı inceleyebilirsiniz.
      </p>
      <p className="text-sm text-center mt-5 text-slate-500">
        Zaten hesabınız var mı? <Link to="/giris" className="text-teal-600 hover:underline">Giriş yapın</Link>
      </p>
    </Shell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/auth/forgot-password", { email });
    setSent(true);
  };
  return (
    <Shell title="Şifre Sıfırlama">
      {sent ? (
        <p className="text-sm text-slate-600" data-testid="forgot-sent">
          E-posta adresiniz kayıtlıysa sıfırlama bağlantısı gönderildi.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>E-posta</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" data-testid="forgot-email" /></div>
          <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 h-11" data-testid="forgot-submit">Bağlantı Gönder</Button>
        </form>
      )}
      <p className="text-sm text-center mt-5"><Link to="/giris" className="text-teal-600 hover:underline">Girişe dön</Link></p>
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), password });
      toast.success("Şifreniz güncellendi");
      navigate("/giris");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Shell title="Yeni Şifre Belirle">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Yeni Şifre</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" data-testid="reset-password" /></div>
        <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 h-11" data-testid="reset-submit">Şifreyi Güncelle</Button>
      </form>
    </Shell>
  );
}
